import { useState } from "react";
import { useDotLegalFileUploadDataMapping } from "../../../common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks";
import { ProcessingActivityDocumentViewModel } from "./ProcessingActivityDocumentTab.types";
import { ProcessingActivityDocumentTabProps } from "./ProcessingActivityDocumentTab.tsx";
import { EntityType } from "../../../common/Common.types";
import { useStateUrlParams } from "../../../common/hooks/useStateUrlParams.ts";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { BestPracticeCheckViewModel } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.types.ts";
import { useTranslation } from "@localization/useTranslation";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { getUniqueFilterOptions, sortByName } from "@common/hooks/filterUtils.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { useAiFeatureAccess } from "../../../plan/aiFeatureGate/useAiFeatureAccess";

export default function useProcessingActivityDocumentTabHooks(props: ProcessingActivityDocumentTabProps) {
    const { translateString } = useTranslation();
    const { showAiQualityAssurance } = useAiFeatureAccess();
    const [selectedDocument, setSelectedDocument] = useState<ProcessingActivityDocumentViewModel>();
    const [showDocumentDialog, setShowDocumentDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showAuditLogDialog, setShowAuditLogDialog] = useStateUrlParams("document-auditlog", null);
    const [bestPracticeCheckDocumentToQualityAssurance, setBestPracticeCheckDocumentToQualityAssurance] = useState<
        ProcessingActivityDocumentViewModel | undefined
    >(undefined);
    const [bestPracticeCheckDocument, setBestPracticeCheckDocument] = useState<ProcessingActivityDocumentViewModel | undefined>(undefined);
    const [bestPracticeCheckInitialResult, setBestPracticeCheckInitialResult] = useState<BestPracticeCheckViewModel | undefined>(undefined);

    const { downloadDocumentVersion } = useDotLegalFileUploadDataMapping();

    const url = `/processingActivity/${props.processingActivityId}/documents`;
    const { isLoading, data, refetch } = useDotLegalQuery<Array<ProcessingActivityDocumentViewModel>>(url);

    const { selectFilters, booleanFilters, dateFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `processingActivity-${props.processingActivityId}-documents-filters`
    );
    const createdFilter = dateFilters.created ?? null;
    const qualityScanFilter = booleanFilters.qualityScan ?? "";

    const documents = applyClientFilters(
        data,
        [
            { id: "name", candidate: (document) => document.name },
            { id: "document", candidate: (document) => document.fileName ?? document.link },
        ],
        selectFilters,
        {},
        filterOperators,
        (document) => {
            if (!matchesDateFilter(createdFilter, document.created)) {
                return false;
            }

            if (qualityScanFilter !== "") {
                return qualityScanFilter === (document.aiQualityAssuranceResult != null);
            }

            return true;
        }
    );

    function getFilterOptions(): Array<FilterOption> {
        const options: Array<FilterOption> = [
            {
                id: "name",
                label: translateString("name"),
                type: FilterOptionType.SelectField,
                value: selectFilters.name ?? [],
                fixedData: getUniqueFilterOptions(data, (document) => document.name).sort(sortByName),
            },
            {
                id: "document",
                label: translateString("document"),
                type: FilterOptionType.SelectField,
                value: selectFilters.document ?? [],
                fixedData: getUniqueFilterOptions(data, (document) => document.fileName ?? document.link).sort(sortByName),
            },
            {
                id: "created",
                label: translateString("created"),
                type: FilterOptionType.DateField,
                value: createdFilter ?? "",
                hideNoDate: true,
            },
        ];

        if (showAiQualityAssurance) {
            options.push({
                id: "qualityScan",
                label: translateString("aiQualityScan"),
                type: FilterOptionType.Boolean,
                value: qualityScanFilter,
            });
        }

        return withOperators(options, filterOperators);
    }

    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0) || createdFilter !== null || qualityScanFilter !== "";

    const onDownloadClick = async (document: ProcessingActivityDocumentViewModel) => {
        await downloadDocumentVersion(document.documentId, document.dataId!, document.fileName!, EntityType.ProcessingActivity, props.processingActivityId!);
    };

    const doRefetch = () => {
        refetch();
        props.refetch();
    };

    return {
        isLoading,
        data,
        documents: documents ?? [],
        hasDocuments: isLoading || data!.length > 0,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        selectedDocument,
        setSelectedDocument,
        showDocumentDialog,
        setShowDocumentDialog,
        showDeleteDialog,
        setShowDeleteDialog,
        refetch: doRefetch,
        onDownloadClick,
        showAuditLogDialog,
        setShowAuditLogDialog,
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
    };
}
